import {
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  ListContext,
  ListContextValue,
  LockedIcon,
  MenuItemType,
  Stack,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import { DataModelEnum, Permission, PermissionAction, StudentServiceIndex } from "generated/openApi";
import { useContext } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const StudentServiceIndexPageHeader = (props: {
  rootPermission: Permission;
  handle: string;
  crudMode: CrudMode;
  reroute: (id: number, crudMode: CrudMode) => void;
  fetch: (id: number) => void;
  index: StudentServiceIndex;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.index.id;

  const save = (index: StudentServiceIndex) => {
    props.setValidationErrors({});

    if (id > 0) {
      ApiService.studentServiceIndexes
        .saveUpdate(index)
        .then(() => {
          props.reroute(id, "view");
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.response);
        });
    } else {
      ApiService.studentServiceIndexes
        .saveCreate(index)
        .then((response) => {
          const id = response;

          if (id && id > 0) {
            props.reroute(id, "view");
            props.fetch(id);
          } else {
            navigate("../");
          }

          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.response);
        });
    }
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.studentServiceIndexes.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const menuItems: MenuItemType[] = [];
  if (id > 0) {
    if (
      isAuthorized(props.rootPermission, PermissionAction.Delete) &&
      (!props.index.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock))
    )
      menuItems.push({
        label: <Typography color="error">Delete</Typography>,
        action: () => postDelete(id),
      });
  }

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        <Typography variant="inherit" sx={{ textTransform: "uppercase" }}>
          {id === 0 ? "New" : ""} {props.handle}
        </Typography>
        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        {props.index.isLocked && <LockedIcon />}

        {id > 0 && <DataModelInformation dataModelType={DataModelEnum.StudentServiceIndex} dataModelId={id} />}

        <CloseButton theme="Dark" confirm={props.unsavedChanges} />

        {props.crudMode === "view" &&
          isAuthorized(props.rootPermission, PermissionAction.Update) &&
          (!props.index.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock)) && (
            <UpdateButton theme="Dark" onClick={() => props.reroute(id, "edit")} />
          )}

        {props.crudMode === "edit" && (
          <SubmitButton theme="Dark" onClick={() => save(props.index)} disabled={!props.unsavedChanges} />
        )}
      </Stack>
    </Stack>
  );
};
